import React, { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { BaseButton } from 'iebt-zing-storybook'
import { Filter } from 'components/Filter'
import { FilterOrderBar } from 'sharedKernel/styledComponents/filterComponents/style'
import { showModal } from 'components/popup'
import { showToast } from 'components/toast'
import { Loading } from 'components/loading'
import { MeetingRoomSolicitationsTable } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/MeetingRoomSolicitationsTable'
import { EditReservationModal } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/EditReservationModal'
import { SolicitationDetailModal } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/SolicitationDetailModal'
import {
  IMeetingRoomSolicitationDetail,
  IMeetingRoomSolicitationRow,
} from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/types'
import {
  useMeetingRoomSolicitationActions,
  useMeetingRoomSolicitationDetail,
  useMeetingRoomSolicitations,
} from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/hooks/useMeetingRoomSolicitations'
import { useMeetingRoomSpaceOptions } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/hooks/useMeetingRoomSpaceOptions'
import {
  MEETING_ROOM_SOLICITATIONS_PAGE_SIZE,
} from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/api/reservationConstants'
import { ListPaginationFooter } from 'domain/feature/hub/company/meeting-room/meeting-room-solicitation/ListPaginationFooter'
import { trans } from 'sharedKernel/i18n'
import {
  MeetingRoomToolbarActions,
  PageHeader,
  PageTitle,
} from '../pageLayoutStyles'

type SolicitationAction = 'approve' | 'reject' | 'cancel'

const CONFIRMATION_CONFIG: Record<
  SolicitationAction,
  { titleKey: string; messageKey: string; toastKey: string }
> = {
  approve: {
    titleKey: 'meetingRoom.reservations.confirm.approveTitle',
    messageKey: 'meetingRoom.reservations.confirm.approveMessage',
    toastKey: 'meetingRoom.reservations.toast.approveSuccess',
  },
  reject: {
    titleKey: 'meetingRoom.reservations.confirm.rejectTitle',
    messageKey: 'meetingRoom.reservations.confirm.rejectMessage',
    toastKey: 'meetingRoom.reservations.toast.rejectSuccess',
  },
  cancel: {
    titleKey: 'meetingRoom.reservations.confirm.cancelTitle',
    messageKey: 'meetingRoom.reservations.confirm.cancelMessage',
    toastKey: 'meetingRoom.reservations.toast.cancelSuccess',
  },
}

export const HubCompanyMeetingRoomRequestsPage = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedSolicitationId, setSelectedSolicitationId] = useState<string | null>(null)
  const [editingSolicitationId, setEditingSolicitationId] = useState<string | null>(null)

  const pageSize = MEETING_ROOM_SOLICITATIONS_PAGE_SIZE

  const { solicitations, totalRows, isLoading, refetch } = useMeetingRoomSolicitations({
    searchTerm: appliedSearchTerm,
    page: currentPage,
    pageSize,
  })
  const { solicitation: selectedSolicitationDetail } =
    useMeetingRoomSolicitationDetail(selectedSolicitationId)
  const { solicitation: editingSolicitationDetail, isLoading: isEditingDetailLoading } =
    useMeetingRoomSolicitationDetail(editingSolicitationId)

  const editingSolicitation = useMemo(() => {
    if (editingSolicitationDetail) {
      return editingSolicitationDetail
    }

    if (!editingSolicitationId) {
      return null
    }

    return solicitations.find(item => item.id === editingSolicitationId) ?? null
  }, [editingSolicitationDetail, editingSolicitationId, solicitations])

  const { spaceOptions, isLoading: isSpaceOptionsLoading } = useMeetingRoomSpaceOptions(
    editingSolicitation?.meetingRoomId
  )

  const selectedSolicitation = useMemo(() => {
    if (selectedSolicitationDetail) {
      return selectedSolicitationDetail
    }

    if (!selectedSolicitationId) {
      return null
    }

    return solicitations.find(item => item.id === selectedSolicitationId) ?? null
  }, [selectedSolicitationDetail, selectedSolicitationId, solicitations])

  const { approve, reject, cancel, edit, exportReservations, isUpdating, isExporting } =
    useMeetingRoomSolicitationActions(refetch)

  const runAction = async (
    action: SolicitationAction,
    row: IMeetingRoomSolicitationRow
  ): Promise<void> => {
    const solicitation = solicitations.find(item => item.id === row.id)
    if (!solicitation) return

    try {
      if (action === 'approve') {
        await approve(solicitation)
      } else if (action === 'reject') {
        await reject(solicitation)
      } else {
        await cancel(solicitation)
      }

      showToast('success', trans(CONFIRMATION_CONFIG[action].toastKey))
    } catch {
      showToast('error', trans('meetingRoom.reservations.toast.actionError'))
    }
  }

  const confirmSolicitationAction = (
    action: SolicitationAction,
    row: IMeetingRoomSolicitationRow
  ): void => {
    const config = CONFIRMATION_CONFIG[action]

    showModal('confirmation', {
      title: trans(config.titleKey),
      message: trans(config.messageKey),
      onConfirm: () => {
        void runAction(action, row)
      },
    })
  }

  const handleApprove = (row: IMeetingRoomSolicitationRow): void => {
    confirmSolicitationAction('approve', row)
  }

  const handleReject = (row: IMeetingRoomSolicitationRow): void => {
    confirmSolicitationAction('reject', row)
  }

  const handleCancel = (row: IMeetingRoomSolicitationRow): void => {
    confirmSolicitationAction('cancel', row)
  }

  const handleEdit = (row: IMeetingRoomSolicitationDetail): void => {
    setEditingSolicitationId(row.id)
  }

  const handleConfirmEdit = async (
    solicitationId: string,
    data: { meetingRoomId: string; roomName: string; startTime: string; endTime: string }
  ): Promise<void> => {
    const solicitation =
      editingSolicitation ??
      solicitations.find(item => item.id === solicitationId) ??
      null
    if (!solicitation) return

    try {
      await edit(solicitation, data)
      showToast('success', trans('meetingRoom.reservations.toast.editSuccess'))
      setEditingSolicitationId(null)
    } catch {
      showToast('error', trans('meetingRoom.reservations.toast.actionError'))
    }
  }

  const handleViewDetails = (row: IMeetingRoomSolicitationDetail): void => {
    setSelectedSolicitationId(row.id)
  }

  const handleCloseDetails = (): void => {
    setSelectedSolicitationId(null)
  }

  const handleSearch = (term: string): void => {
    setSearchTerm(term)
    setAppliedSearchTerm(term)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number): void => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      <PageHeader>
        <PageTitle>Gerenciar solicitações</PageTitle>
      </PageHeader>

      <FilterOrderBar>
        <Filter
          currentSearchTerm={searchTerm}
          btnClick={handleSearch}
          onFilterChange={setSearchTerm}
          minSearchTermLength={1}
        />
        <MeetingRoomToolbarActions>
          <BaseButton
            variant="primary-invert"
            disabled={isExporting}
            onClick={() => {
              void exportReservations()
            }}
            btnContent={
              <span>
                <FontAwesomeIcon className="pr-2" icon={['fas', 'download']} />
                Exportar
              </span>
            }
          />
        </MeetingRoomToolbarActions>
      </FilterOrderBar>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <MeetingRoomSolicitationsTable
            rows={solicitations}
            searchTerm=""
            onViewDetails={handleViewDetails}
            onEdit={handleEdit}
            onApprove={handleApprove}
            onReject={handleReject}
            onCancel={handleCancel}
          />
          <ListPaginationFooter
            currentPage={currentPage}
            pageSize={pageSize}
            totalRows={totalRows}
            onPageChange={handlePageChange}
          />
        </>
      )}

      <EditReservationModal
        isOpen={editingSolicitationId !== null}
        solicitation={editingSolicitation}
        spaceOptions={spaceOptions}
        isSubmitting={isUpdating}
        isLoading={isEditingDetailLoading || isSpaceOptionsLoading}
        onClose={() => setEditingSolicitationId(null)}
        onConfirm={(solicitationId, data) => {
          void handleConfirmEdit(solicitationId, data)
        }}
      />

      <SolicitationDetailModal
        solicitation={selectedSolicitation}
        onClose={handleCloseDetails}
      />
    </>
  )
}
